import { SxProps } from '@mui/material';
import { Theme } from '@mui/system';
import { useEffect, useMemo } from 'react';

import { AdditionalItemRendererProps } from '@/components/form/formAwareInputs/FormAwareInput.types';
import { SuggestionForSearchAndEvidence } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { LocateSuggestionButton } from '@/modules/aiSuggestions/components/LocateSuggestionButton';
import { useSelectedSuggestionEvidenceContext } from '@/modules/aiSuggestions/context/SelectedSuggestionEvidence.context';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { AiSuggestionAcceptanceStatus } from '@/types/schema';

// This enum tells us the use-case of the icon, which helps us determine
// conditional rendering logic.
export enum FormAwareLocateSuggestionButtonVariant {
  // The icon is being used in a matcher row. We will render the icon as long as
  // the suggestion has NOT been rejected.
  MATCHER = 'MATCHER',
  // The icon is being used in a field. We will render the icon ONLY if the
  // suggestion has been accepted.
  FIELD = 'FIELD',
}

interface FormAwareLocateSuggestionButtonProps {
  suggestion: SuggestionForSearchAndEvidence | undefined;
  variant: FormAwareLocateSuggestionButtonVariant;
  additionalItemRendererProps?: Omit<
    AdditionalItemRendererProps,
    'mainLabelOverride'
  >;
  sx?: SxProps<Theme>;
}

/**
 * A FormAware IconButton to with a magnifying glass icon to locate evidence for
 * a suggestion. This component should be used to locate a suggestion for a
 * specific field input.
 *
 * @param suggestion
 * @param variant
 * @param additionalItemRendererProps
 * @param sx
 * @constructor
 */
export function FormAwareLocateSuggestionButton({
  suggestion,
  variant,
  additionalItemRendererProps,
  sx,
}: FormAwareLocateSuggestionButtonProps) {
  const { acknowledgedSuggestions } = useSuggestionAcknowledgementContext();
  const { locateSuggestion } = useSelectedSuggestionEvidenceContext();

  const willRender = useMemo(() => {
    if (variant === FormAwareLocateSuggestionButtonVariant.MATCHER) {
      // If the icon is used in a matcher row, we will render the icon as long
      // as the suggestion has NOT been rejected.
      return (
        suggestion &&
        acknowledgedSuggestions[suggestion.id]?.status !==
          AiSuggestionAcceptanceStatus.Rejected
      );
    }

    // If the icon is used in a field, we will render the icon ONLY if the
    // suggestion has been accepted or auto-accepted.
    return (
      suggestion &&
      (acknowledgedSuggestions[suggestion.id]?.status ===
        AiSuggestionAcceptanceStatus.Accepted ||
        acknowledgedSuggestions[suggestion.id]?.status ===
          AiSuggestionAcceptanceStatus.AutoAccepted) &&
      !additionalItemRendererProps?.hideLocateSuggestionIconOverride
    );
  }, [
    acknowledgedSuggestions,
    additionalItemRendererProps?.hideLocateSuggestionIconOverride,
    suggestion,
    variant,
  ]);

  useEffect(() => {
    if (!additionalItemRendererProps) {
      return;
    }

    // If we render the locator icon, we need to pad the top of the "delete"
    // button when rendering the field in a FormAwareInputRepeater.
    additionalItemRendererProps.onRenderHasLocatorButton?.(
      variant === FormAwareLocateSuggestionButtonVariant.FIELD && !!willRender
    );
  }, [additionalItemRendererProps, variant, willRender]);

  if (!willRender || !suggestion) {
    return null;
  }

  return (
    <LocateSuggestionButton
      suggestionID={suggestion.id}
      onLocateSuggestionClick={() => locateSuggestion(suggestion)}
      ariaLabel="Locate suggestion"
      dataTestId={`locateSuggestionIcon-${suggestion.kind}`}
      sx={sx}
    />
  );
}
